import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { getGuestXp, googleLoginHref, PROMPT_INTERVAL_MS, XP_EVENT, XP_PROMPT_KEY, SHOW_SIGNIN_PROMPT } from './xp';
import GoogleSignInButton from './GoogleSignInButton';

function readPromptState() {
  try {
    return JSON.parse(sessionStorage.getItem(XP_PROMPT_KEY) || '{}');
  } catch {
    return {};
  }
}

const GuestXpPrompt = ({ isAuthenticated, authReady }) => {
  const [open, setOpen] = useState(false);
  const [guestXp, setGuestXp] = useState(getGuestXp);
  const [promptReason, setPromptReason] = useState(null);

  useEffect(() => {
    const onXp = (e) => setGuestXp(e.detail?.xp ?? getGuestXp());
    window.addEventListener(XP_EVENT, onXp);
    const onStorage = (e) => {
      if (e.key === 'bigplays:guestXp') setGuestXp(getGuestXp());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(XP_EVENT, onXp);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    if (!authReady || isAuthenticated) {
      setOpen(false);
      setPromptReason(null);
      return undefined;
    }
    if (window.location.pathname === '/create-account'
      || window.location.pathname === '/connect-google'
      || window.location.pathname === '/privacy'
      || window.location.pathname === '/terms'
      || window.location.pathname === '/report') {
      setOpen(false);
      return undefined;
    }

    const maybeShow = () => {
      const { dismissedAt } = readPromptState();
      if (!dismissedAt || Date.now() - dismissedAt >= PROMPT_INTERVAL_MS) {
        setGuestXp(getGuestXp());
        setPromptReason(null);
        setOpen(true);
      }
    };

    maybeShow();
    const timer = setInterval(maybeShow, 15 * 1000);
    return () => clearInterval(timer);
  }, [authReady, isAuthenticated]);

  useEffect(() => {
    if (!authReady || isAuthenticated) return undefined;
    const onShow = (e) => {
      setGuestXp(getGuestXp());
      setPromptReason(e.detail?.reason || null);
      setOpen(true);
    };
    window.addEventListener(SHOW_SIGNIN_PROMPT, onShow);
    return () => window.removeEventListener(SHOW_SIGNIN_PROMPT, onShow);
  }, [authReady, isAuthenticated]);

  const dismissForSession = () => {
    sessionStorage.setItem(XP_PROMPT_KEY, JSON.stringify({ dismissedAt: Date.now() }));
    setOpen(false);
    setPromptReason(null);
  };

  if (!open || isAuthenticated) return null;

  const googleHref = googleLoginHref();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[200] p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5 relative text-white">
        <button
          onClick={dismissForSession}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
          aria-label="Continue as guest"
        >
          <FaTimes size={20} />
        </button>

        <h2 className="text-xl font-bold mb-2 pr-8">
          {promptReason === 'clip' ? 'Sign in to save clips' : promptReason === 'guest-chat' ? 'Sign in to keep chatting' : 'Save your progress'}
        </h2>
        <p className="text-gray-300 mb-4">
          {promptReason === 'clip' ? (
            <>Clips need an account so we can credit you. Sign in or create an account to save this clip.</>
          ) : promptReason === 'guest-chat' ? (
            <>Guests can send 5 hall messages. Sign in or create an account to keep chatting.</>
          ) : (
            <>
              You have <span className="text-yellow-400 font-semibold">{guestXp} XP</span>.
              Sign in to keep it, plus watch history, hall progress, and future rewards.
            </>
          )}
        </p>
        <ul className="text-sm text-gray-400 mb-5 list-disc pl-5 space-y-1">
          <li>XP and upcoming cosmetics, badges, and predictions</li>
          <li>Watch history across devices</li>
          <li>Hall progress</li>
        </ul>

        <div className="flex flex-col items-center gap-3">
          <GoogleSignInButton href={googleHref} />
          <a
            href={googleHref}
            className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-gray-600 hover:bg-gray-500 text-white text-sm font-semibold text-center w-full"
          >
            Create Account
          </a>
          <button
            type="button"
            onClick={dismissForSession}
            className="text-gray-400 hover:text-white py-2"
          >
            Continue as Guest
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestXpPrompt;
